import React, { useEffect, useState } from "react";
import "./styles/index.scss";
import * as api from "../../../../../api/generated-api";
import {
  isCompanyMemberOrCounselForDeal,
  toValidIdentifier,
} from "../../../../../helpers/utilsTS";
import CheckBox from "../../../../common/forms/checkbox";
import SignedIcon from "../../../../common/icons/SignedIcon";
import COLORS from "../../../../../colors";
import { UserWithPersonWithOrganizationPersons } from "../../../../../api/types";

export type DealMemberSigner = {
  legalEntityType: api.LegalEntityType;
  title: string;
  subTitle: string;
  signerId: number;
  status: api.SigningStatus;
  dealLegalEntityId: number;
  investorId?: number;
  hasNewCashInvestment: boolean;
  hasSafeInvestment: boolean;
};

type DocumentsSignersGroupPropsType = {
  orgId: number;
  user: UserWithPersonWithOrganizationPersons;
  dealId: number;
  title: string;
  dealMemberSigners: DealMemberSigner[];
  addSigners: (dealMemberSigners: DealMemberSigner[]) => void;
  removeSigners: (dealMemberSigners: DealMemberSigner[]) => void;
  dealLegalEntitiesForUser: api.DealLegalEntity[];
};

const DocumentsSignersGroup = ({
  orgId,
  user,
  dealId,
  title,
  dealMemberSigners,
  addSigners,
  removeSigners,
  dealLegalEntitiesForUser,
}: DocumentsSignersGroupPropsType) => {
  const [checked, setChecked] = useState(false);
  const [checkedPartial, setCheckedPartial] = useState(false);

  const isUserCompanyMember = isCompanyMemberOrCounselForDeal(
    user.person.organizations_persons,
    orgId,
    dealId,
    dealLegalEntitiesForUser
  );

  useEffect(() => {
    const dealMemberSignersUnsigned = dealMemberSigners.filter(
      (s) => s.status !== "signed"
    );
    if (
      dealMemberSignersUnsigned.filter(
        (documentSigner) => !!documentSigner.signerId
      ).length === 0
    ) {
      setChecked(false);
      setCheckedPartial(false);
    } else if (
      dealMemberSignersUnsigned.filter(
        (documentSigner) => !!documentSigner.signerId
      ).length === dealMemberSignersUnsigned.length
    ) {
      setChecked(true);
      setCheckedPartial(false);
    } else {
      setChecked(false);
      setCheckedPartial(true);
    }
  }, [dealMemberSigners]);

  return (
    <div data-testid="singer-checkbox-group" className="signer-group">
      <div
        className="signer-group_checkbox-container"
        data-testid={`group-add-signer-checkbox_${toValidIdentifier(title)}`}
      >
        <CheckBox
          checked={checked}
          disabled={!isUserCompanyMember}
          indeterminate={checkedPartial}
          classLabel="signer-group_label"
          onChange={() => {
            if (!checked && !checkedPartial) {
              addSigners(
                dealMemberSigners.filter((s) => s.status !== "signed")
              );
            } else {
              removeSigners(
                dealMemberSigners.filter(
                  (s) => s.status && s.status !== "signed"
                )
              );
            }
          }}
          isNew={true}
        >
          <div>{title}</div>
        </CheckBox>
      </div>
      <div className="signer-group_items-container">
        {dealMemberSigners.map((dealMemberSigner, i) => {
          return (
            <div
              key={i}
              className="signer-group_item_container"
              data-testid="row-document-signer-checkbox"
              data-test-checked={!!dealMemberSigner.signerId}
            >
              <>
                {dealMemberSigner.status === "signed" && (
                  <div
                    className="signer-group_checkbox-container"
                    data-testid="signed-signer"
                  >
                    <div className="signed-icon-container">
                      <SignedIcon fill={COLORS.TERTIARY_COLOR_900} />
                    </div>
                    <div>{dealMemberSigner.title}</div>
                  </div>
                )}
                {dealMemberSigner.status !== "signed" && (
                  <div className="signer-group_checkbox-container">
                    <CheckBox
                      data-testid="single-add-signer-checkbox"
                      disabled={!isUserCompanyMember}
                      classLabel="signer-group_item-label"
                      checked={!!dealMemberSigner.signerId}
                      onChange={() => {
                        if (!!dealMemberSigner.signerId === false) {
                          addSigners([dealMemberSigner]);
                        } else {
                          removeSigners([dealMemberSigner]);
                        }
                      }}
                      isNew={true}
                    >
                      <div>{dealMemberSigner.title}</div>
                    </CheckBox>
                  </div>
                )}

                {dealMemberSigner.subTitle &&
                  dealMemberSigner.subTitle.length && (
                    <div className="signer-group_item-sub-label">
                      {dealMemberSigner.subTitle}
                    </div>
                  )}
              </>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DocumentsSignersGroup;
